import { useState, useMemo, useEffect } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Chip, Button, IconButton, Checkbox, Collapse,
  CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon  from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';

const STATUS_COLOR = {
  compliant: '#4caf50', partial: '#ffeb3b', non_compliant: '#f44336',
  na: '#7a8fa6', not_assessed: '#4a5a70',
};
const STATUS_LABEL = {
  compliant: 'Compliant', partial: 'Partial', non_compliant: 'Non-Compliant',
  na: 'N/A', not_assessed: 'Not Assessed',
};

function StatusPill({ value, dim }) {
  const c = STATUS_COLOR[value] || '#7a8fa6';
  return (
    <Chip
      label={STATUS_LABEL[value] || value || '—'}
      size="small"
      sx={{
        height: 18, fontSize: 10, fontWeight: 700,
        color: c, backgroundColor: `${c}${dim ? '11' : '22'}`,
        border: `1px solid ${c}${dim ? '22' : '55'}`,
      }}
    />
  );
}

function DiffRow({ row, checked, onToggle }) {
  const [open, setOpen] = useState(false);
  const statusChanged = row.proposed_status !== row.current_status;
  const notesChanged  = (row.proposed_notes || '') !== (row.current_notes || '');
  return (
    <Box sx={{ borderBottom: '1px solid #1a2840' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '36px 28px 110px 1fr 110px 24px 110px 28px',
        gap: 1, alignItems: 'center', padding: '8px 10px',
        backgroundColor: row.will_change ? '#0d1f33' : 'transparent',
      }}>
        <Checkbox
          size="small"
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={!row.will_change}
          sx={{ color: '#7a8fa6', '&.Mui-checked': { color: '#00bcd4' } }}
        />
        <IconButton size="small" onClick={() => setOpen(o => !o)} sx={{ color: '#7a8fa6', padding: 0 }}>
          {open ? <KeyboardArrowDownIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
        </IconButton>
        <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
          {row.nist_id}
        </Typography>
        <Typography sx={{ color: '#c0cfe0', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <StatusPill value={row.current_status} dim />
        </Box>
        <ArrowForwardIcon sx={{ fontSize: 14, color: statusChanged ? '#00bcd4' : '#2a3a50' }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <StatusPill value={row.proposed_status} />
        </Box>
        <Typography sx={{ color: notesChanged ? '#00bcd4' : '#2a3a50', fontSize: 10, textAlign: 'center' }} title={notesChanged ? 'Notes will change' : 'Notes unchanged'}>
          {notesChanged ? '●' : '○'}
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, padding: '12px 16px', backgroundColor: '#070c14', borderTop: '1px solid #1a2840' }}>
          <Box>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Current notes</Typography>
            <Typography sx={{ color: '#a0b4c8', fontSize: 12, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {row.current_notes || <Box component="span" sx={{ color: '#2a3a50', fontStyle: 'italic' }}>(empty)</Box>}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ color: '#00bcd4', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Proposed notes (from SSP)</Typography>
            <Typography sx={{ color: '#c0cfe0', fontSize: 12, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {row.proposed_notes || <Box component="span" sx={{ color: '#2a3a50', fontStyle: 'italic' }}>(empty)</Box>}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function SspDiffDialog({ open, onClose, diff, applying, onApply }) {
  const changeableIds = useMemo(
    () => (diff?.matched || []).filter(r => r.will_change).map(r => r.id),
    [diff]
  );
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (open) setSelected(new Set(changeableIds));  // default: apply all changes
  }, [open, changeableIds]);

  if (!diff) return null;
  const { ssp, summary, matched, unmatched, no_nist_mapping, new_in_ssp } = diff;

  const toggleAll = (on) => setSelected(on ? new Set(changeableIds) : new Set());
  const toggleOne = (id, on) => setSelected(prev => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const allSelected = changeableIds.length > 0 && selected.size === changeableIds.length;
  const noneSelected = selected.size === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#0d1825', border: '1px solid #1e3a5f', maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid #1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px' }}>
        <Box>
          <Typography sx={{ color: '#ffffff', fontSize: 16, fontWeight: 700 }}>
            SSP Diff Review
          </Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, fontFamily: 'monospace', mt: 0.25 }}>
            {ssp?.filename}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#7a8fa6' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        {/* Summary bar */}
        <Box sx={{ display: 'flex', gap: 1, padding: '12px 20px', borderBottom: '1px solid #1e3a5f', backgroundColor: '#111827', flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={`${summary.matched} matched`}              size="small" sx={{ height: 22, color: '#c0cfe0', backgroundColor: '#1e3a5f' }} />
          <Chip label={`${summary.will_change} will change`}      size="small" sx={{ height: 22, color: '#00bcd4', backgroundColor: '#00bcd422', border: '1px solid #00bcd455', fontWeight: 700 }} />
          <Chip label={`${summary.unchanged} unchanged`}          size="small" sx={{ height: 22, color: '#7a8fa6', backgroundColor: '#1e3a5f' }} />
          <Chip label={`${summary.unmatched} not in SSP`}         size="small" sx={{ height: 22, color: '#ffeb3b', backgroundColor: '#ffeb3b22', border: '1px solid #ffeb3b55' }} />
          <Chip label={`${summary.no_nist} non-NIST (skipped)`}   size="small" sx={{ height: 22, color: '#7a8fa6', backgroundColor: '#1e3a5f' }} />
          <Chip label={`${summary.new_in_ssp} new in SSP`}        size="small" sx={{ height: 22, color: '#9c27b0', backgroundColor: '#9c27b022', border: '1px solid #9c27b055' }} />
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => toggleAll(true)}  disabled={allSelected}  sx={{ color: '#00bcd4', fontSize: 11 }}>Apply all</Button>
          <Button size="small" onClick={() => toggleAll(false)} disabled={noneSelected} sx={{ color: '#7a8fa6', fontSize: 11 }}>Apply none</Button>
        </Box>

        {/* Column headers */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '36px 28px 110px 1fr 110px 24px 110px 28px',
          gap: 1, padding: '6px 10px', borderBottom: '1px solid #1e3a5f', backgroundColor: '#0a1525',
        }}>
          {['', '', 'Control', 'Title', 'Current', '', 'Proposed', 'Δ'].map((h, i) => (
            <Typography key={i} sx={{ color: '#7a8fa6', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</Typography>
          ))}
        </Box>

        {/* Rows */}
        <Box sx={{ maxHeight: '52vh', overflowY: 'auto' }}>
          {matched.length === 0 ? (
            <Typography sx={{ color: '#4a5a70', textAlign: 'center', padding: 4, fontStyle: 'italic' }}>
              No matching controls found in the SSP.
            </Typography>
          ) : (
            matched.map(row => (
              <DiffRow
                key={row.id}
                row={row}
                checked={selected.has(row.id)}
                onToggle={(on) => toggleOne(row.id, on)}
              />
            ))
          )}
        </Box>

        {/* Informational sections */}
        {(unmatched.length > 0 || new_in_ssp.length > 0 || no_nist_mapping.length > 0) && (
          <Box sx={{ padding: '12px 20px', borderTop: '1px solid #1e3a5f', backgroundColor: '#0a1525' }}>
            {unmatched.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1, backgroundColor: '#2a2410', color: '#ffeb3b', fontSize: 12 }}>
                <strong>{unmatched.length} register entries not addressed by this SSP:</strong>{' '}
                {unmatched.slice(0, 6).map(u => u.reference.replace(/^NIST 800-53 /, '')).join(', ')}
                {unmatched.length > 6 ? ` … +${unmatched.length - 6} more` : ''}
              </Alert>
            )}
            {new_in_ssp.length > 0 && (
              <Alert severity="info" sx={{ mb: 1, backgroundColor: '#160a2a', color: '#9c27b0', fontSize: 12 }}>
                <strong>{new_in_ssp.length} controls in the SSP but not in this register:</strong>{' '}
                {new_in_ssp.slice(0, 8).map(n => n.nist_id).join(', ')}
                {new_in_ssp.length > 8 ? ` … +${new_in_ssp.length - 8} more` : ''}
              </Alert>
            )}
            {no_nist_mapping.length > 0 && (
              <Alert severity="info" sx={{ backgroundColor: '#0a1825', color: '#7a8fa6', fontSize: 12, border: '1px solid #1e3a5f' }}>
                <strong>{no_nist_mapping.length} entries skipped</strong> (DoD-only references without a NIST control ID — eMASS doesn't carry these).
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #1e3a5f', padding: '12px 20px', backgroundColor: '#0a1525' }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 12, mr: 'auto' }}>
          {selected.size} of {changeableIds.length} change{changeableIds.length === 1 ? '' : 's'} selected
        </Typography>
        <Button onClick={onClose} disabled={applying} sx={{ color: '#7a8fa6', fontSize: 12 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onApply([...selected])}
          disabled={applying || selected.size === 0}
          startIcon={applying ? <CircularProgress size={14} sx={{ color: '#0a0e1a' }} /> : null}
          sx={{
            backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, fontSize: 12,
            '&:hover': { backgroundColor: '#00acc1' },
            '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' },
          }}
        >
          {applying ? 'Applying…' : `Apply ${selected.size} change${selected.size === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
